import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

export default function PrivacyPage() {
  return (
    <div className="min-h-[100dvh] bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <Link href="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors font-mono">
            <ArrowLeft size={14} />
            Back to home
          </Link>
        </div>

        <div className="flex items-center gap-3 mb-10">
          <img
            src={`${basePath}/pa-icon.jpg`}
            alt="Podcasts Abbreviated"
            className="h-8 w-8 rounded-sm object-cover"
          />
          <span className="font-mono font-bold tracking-tight uppercase text-sm text-foreground">
            Podcasts <span className="text-primary">Abbreviated</span>
          </span>
        </div>

        <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground text-sm mb-10">Last updated: April 25, 2025</p>

        <div className="prose prose-invert max-w-none space-y-8 text-sm leading-7 text-muted-foreground">

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">1. Introduction</h2>
            <p>
              Podcasts Abbreviated ("we", "us", or "our") provides a service that transcribes and
              summarizes podcast audio into concise text clips. This Privacy Policy explains how we
              collect, use, and protect information when you use our website and services.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">2. Information We Collect</h2>
            <h3 className="font-medium text-foreground mb-2">Account Information</h3>
            <p className="mb-4">
              When you create an account we collect your name, email address, and authentication
              credentials managed by Clerk (our identity provider). We do not store raw passwords.
            </p>
            <h3 className="font-medium text-foreground mb-2">Podcast and Feed Data</h3>
            <p className="mb-4">
              We collect RSS feed URLs you submit, episode audio files you upload or link, and the
              resulting transcripts and summaries produced by our processing pipeline.
            </p>
            <h3 className="font-medium text-foreground mb-2">Connected Social Accounts (YouTube, LinkedIn)</h3>
            <p>
              If you choose to connect a YouTube or LinkedIn account, we store OAuth access tokens
              and refresh tokens (encrypted at rest) along with your display name associated with
              that account. These tokens are used solely to publish content on your behalf when you
              explicitly initiate an upload. We request the following Google/YouTube scopes:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
              <li><code className="text-xs bg-muted px-1 py-0.5 rounded">https://www.googleapis.com/auth/youtube.upload</code> — to upload videos to your YouTube channel</li>
              <li><code className="text-xs bg-muted px-1 py-0.5 rounded">https://www.googleapis.com/auth/youtube</code> — to manage YouTube resources needed for publishing</li>
              <li><code className="text-xs bg-muted px-1 py-0.5 rounded">https://www.googleapis.com/auth/userinfo.profile</code> — to display your name in the app</li>
            </ul>
            <p className="mt-3">
              We do not use these tokens for any purpose other than the explicit upload actions you
              initiate. We do not read, analyze, or share your YouTube channel content or subscriber data.
              You can revoke access at any time by disconnecting the account in the app or via your
              Google Account settings at <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">myaccount.google.com/permissions</a>.
            </p>
            <h3 className="font-medium text-foreground mb-2 mt-4">Usage Data</h3>
            <p>
              We collect server logs, error reports, and basic usage metrics (such as job processing
              times and feature usage counts) to operate and improve the service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>To process podcast audio and deliver transcripts and summaries to you</li>
              <li>To authenticate you and maintain your account</li>
              <li>To publish clips to connected social accounts when you request it</li>
              <li>To send transactional notifications (e.g. job completion emails) if you opt in</li>
              <li>To diagnose issues and improve service reliability</li>
              <li>To process payments via Stripe for paid plans</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">4. Data Sharing</h2>
            <p className="mb-3">
              We do not sell your personal data. We share data only with the following service providers
              as necessary to operate the service:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong className="text-foreground">Clerk</strong> — identity and authentication</li>
              <li><strong className="text-foreground">Stripe</strong> — payment processing</li>
              <li><strong className="text-foreground">OpenAI / Whisper</strong> — audio transcription</li>
              <li><strong className="text-foreground">Replit</strong> — cloud infrastructure and hosting</li>
              <li><strong className="text-foreground">Google / YouTube</strong> — video publishing when you connect a YouTube account</li>
              <li><strong className="text-foreground">LinkedIn</strong> — post publishing when you connect a LinkedIn account</li>
            </ul>
            <p className="mt-3">
              Each provider processes your data under their own privacy policies and data processing agreements.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">5. Data Retention</h2>
            <p>
              Processed audio files and output clips are retained for a limited period and
              automatically deleted thereafter. OAuth tokens for connected social accounts are
              retained until you disconnect the account. Account data is retained while your account
              is active and deleted upon account closure.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">6. Security</h2>
            <p>
              We encrypt OAuth tokens at rest and transmit all data over TLS. We implement
              reasonable technical and organizational measures to protect your information, but no
              system is completely secure.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">7. Your Rights</h2>
            <p>
              You may request access to, correction of, or deletion of your personal data by
              contacting us. You may disconnect any linked social account at any time from the
              Subscriptions page within the app.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">8. Changes to This Policy</h2>
            <p>
              We may update this policy from time to time. We will notify you of material changes
              via email or an in-app notice. Continued use of the service after changes constitutes
              acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">9. Contact</h2>
            <p>
              If you have questions about this Privacy Policy, please visit our{" "}
              <Link href="/help" className="text-primary hover:underline">Help page</Link>{" "}
              or use the chat support widget to reach us directly.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
